import { get } from 'svelte/store';

import { getHomeDisplayName } from 'src/helpers/houses/homes_helpers';
import { getSubUnits } from 'src/services/iotu/house-management';
import { user_data, user_settings } from 'src/stores/account/user';
import { sub_units } from 'src/stores/account/home';

import type { NotificationActionUI } from 'src/types/NotificationActionUI';
import type { ISceneObj } from 'src/types/SceneType';
import type { SubUnitData, SubUnitMap } from 'src/types/SubUnit';

import { notificationTypeSupportsRecipients } from 'src/types/NotificationActionUI';
import type {
	ChannelRecipientSummary,
	NotificationChannel,
	SceneRecipientSummary,
	SubUnitRecipientOption,
} from 'src/types/NotificationRecipients';

const PUSH_NOTIFICATION_TYPES = new Set<NotificationActionUI['type']>(['push_notification']);
const MAIL_NOTIFICATION_TYPES = new Set<NotificationActionUI['type']>(['mail_notification']);

const NOTIFICATION_TYPE_BY_CHANNEL: Record<NotificationChannel, NotificationActionUI['type']> = {
	P: 'push_notification',
	M: 'mail_notification',
};

/**
 * Lowercases + trims an email value for safe comparison/display.
 */
export const normalize = (email: string | undefined | null): string =>
	(email ?? '').trim().toLowerCase();

/**
 * Returns the email of the home owner from the perspective of the current
 * session. Owner sessions expose their own email on `user_data`; sub-unit
 * sessions track the owner's email under `user_settings.main_acc`.
 */
export const getHomeOwnerEmail = (): string => {
	const settings = get(user_settings) as Record<string, unknown> | undefined;
	const ownerFromSettings = typeof settings?.main_acc === 'string' ? settings.main_acc : '';
	if (ownerFromSettings) return normalize(ownerFromSettings);
	const data = get(user_data);
	return normalize(data?.email);
};

/**
 * Returns the email of the currently logged-in account.
 * For sub-unit sessions this is the sub-unit's own email; otherwise the owner's.
 */
export const getCurrentAccountEmail = (): string => normalize(get(user_data)?.email);

/**
 * Returns a display name for the currently logged-in account, falling back to
 * the email when no profile name is set. Mirrors the conventions used by
 * `login_helpers` and the profile dropdown.
 */
export const getCurrentUserDisplayName = (): string => {
	const settings = get(user_settings) as Record<string, unknown> | undefined;
	const name = typeof settings?.name === 'string' ? settings.name.trim() : '';
	if (name) return name;
	const email = get(user_data)?.email;
	return typeof email === 'string' ? email : '';
};

/**
 * Returns true when the active session is a sub-unit session.
 */
export const isSubUnitSession = (): boolean => {
	const settings = get(user_settings) as Record<string, unknown> | undefined;
	return typeof settings?.sub === 'number';
};

/**
 * When the current session is a sub-unit, returns the numeric `sub` id from
 * `user_settings`. Owner sessions return `undefined`.
 */
export const getCurrentSubUnitId = (): number | undefined => {
	const settings = get(user_settings) as Record<string, unknown> | undefined;
	return typeof settings?.sub === 'number' ? (settings.sub as number) : undefined;
};

/**
 * Maps a notification UI type to its `nperm` channel flag, or `undefined` for
 * notification types that do not target an individual user (event log, Alexa).
 */
export const channelForNotificationType = (
	type: NotificationActionUI['type'] | string | undefined,
): NotificationChannel | undefined => {
	if (PUSH_NOTIFICATION_TYPES.has(type as NotificationActionUI['type'])) return 'P';
	if (MAIL_NOTIFICATION_TYPES.has(type as NotificationActionUI['type'])) return 'M';
	return undefined;
};

const subUnitName = (data: SubUnitData): string => {
	const meta = data?.[0];
	return getHomeDisplayName(meta?.name, meta?.email ?? '');
};

const subUnitHasChannel = (data: SubUnitData, channel: NotificationChannel): boolean => {
	const nperm = data?.[0]?.nperm ?? '';
	return nperm.includes(channel);
};

/**
 * Parses a `sub_units` map key into a numeric sub-unit id.
 *
 * The store keys the roster as `sub_<id>` (see `SubUnitManagement.svelte`),
 * so the prefix has to be stripped before coercion. Returns `undefined` for
 * keys that are not safely convertible (defensive — should not happen in
 * production data).
 */
const parseSubId = (key: string): number | undefined => {
	const raw = key.replace(/^sub_/, '');
	const id = Number(raw);
	return Number.isFinite(id) ? id : undefined;
};

/**
 * Lists every sub-unit attached to the active home as a recipient row that the
 * wizard can render. Owner is intentionally excluded — owner delivery is
 * controlled per-action via `pushSkipUnit`.
 *
 * @param channel - Notification channel used to evaluate the `nperm` flag.
 */
export const getEligibleSubUnitRecipients = (channel: NotificationChannel): SubUnitRecipientOption[] => {
	const subs = (get(sub_units) ?? {}) as SubUnitMap;
	const currentEmail = getCurrentAccountEmail();
	const currentSubId = getCurrentSubUnitId();

	const recipients: SubUnitRecipientOption[] = [];

	for (const [key, data] of Object.entries(subs)) {
		const subId = parseSubId(key);
		if (subId === undefined) continue;
		const email = normalize(data?.[0]?.email);
		recipients.push({
			subId,
			email,
			name: subUnitName(data) || email,
			hasChannelPermission: subUnitHasChannel(data, channel),
			isCurrentUser: currentSubId !== undefined ? currentSubId === subId : email === currentEmail,
		});
	}

	return recipients;
};

/**
 * Returns true when the active home has at least one shared sub-unit and the
 * recipient picker should be progressively disclosed.
 */
export const hasShareableHome = (): boolean => {
	return Object.keys(get(sub_units) ?? {}).length > 0;
};

/**
 * Module-scoped promise used to deduplicate in-flight `get_sub_units` calls
 * when several display-only consumers (preview, scenes list badge, wizard)
 * happen to mount in the same tick on a fresh page load.
 */
let subUnitsLoadPromise: Promise<void> | null = null;

/**
 * Monotonic token bumped by `resetSubUnitsCache()`. In-flight fetches capture
 * the value at start and refuse to write back if the token has advanced —
 * this prevents a stale request initiated by the previous account from
 * leaking sub-units into the next account's store after a logout / profile
 * switch.
 */
let subUnitsLoadToken = 0;

/**
 * Hydrates the `sub_units` store so consumers that only render recipient
 * names — the side-panel/drawer preview, the wizard's recipient selector,
 * the scenes list — see the latest roster.
 *
 * Behaviour is **stale-while-revalidate**:
 * - No-op for sub-unit sessions: the BE endpoint is owner-only.
 * - When the store already has entries, the cached values stay visible so
 *   there is no flash of empty UI, but a fresh `get_sub_units` request is
 *   still fired in the background and overwrites the store on success.
 *   That way newly invited sub-units (added from the Sharing screen, or
 *   from another tab / device) show up the next time the wizard opens
 *   without requiring a hard refresh.
 * - In-flight requests are coalesced via `subUnitsLoadPromise` so several
 *   consumers mounting in the same tick only trigger one network call.
 * - Responses from a fetch that started before a logout / profile switch
 *   are discarded via the `subUnitsLoadToken` guard.
 *
 * Callers can `await` when they need the freshest data synchronously; the
 * preview / wizard just fire-and-forget from `onMount` and rely on the
 * reactive `$sub_units` subscription to re-render once the data lands.
 */
export const ensureSubUnitsLoaded = async (): Promise<void> => {
	if (isSubUnitSession()) return;
	if (subUnitsLoadPromise) return subUnitsLoadPromise;

	const myToken = subUnitsLoadToken;
	subUnitsLoadPromise = (async () => {
		try {
			const response = await getSubUnits();
			// Guard against a logout / profile switch that happened while the
			// request was in flight — discard the response in that case.
			if (myToken !== subUnitsLoadToken) return;
			if (response?.isok) {
				// Always overwrite (instead of merging) so removed sub-units
				// disappear in addition to newly added ones surfacing.
				sub_units.set(response.data ?? {});
			}
		} catch {
			// Non-blocking: cached values (if any) stay visible; otherwise the
			// preview falls back to the `Sub-unit #<id>` label.
		} finally {
			if (myToken === subUnitsLoadToken) {
				subUnitsLoadPromise = null;
			}
		}
	})();

	return subUnitsLoadPromise;
};

/**
 * Invalidates the sub-units cache and any in-flight `get_sub_units` request.
 *
 * Wired into the account-level clears (`clearAllData`, `clearDataOnLogin`)
 * so logging out, switching profiles, or selecting a different home from a
 * multi-unit account never carries the previous owner's sub-unit list into
 * the new session — which would otherwise show ghost recipients in the
 * notification wizard and the side-panel preview.
 */
export const resetSubUnitsCache = (): void => {
	subUnitsLoadToken += 1;
	subUnitsLoadPromise = null;
	sub_units.reset();
};

/**
 * Default `toSub` list for a freshly-created notification action: empty.
 *
 * The owner is implicitly included via the scene-level `pushSkipUnit` flag
 * (which defaults to falsy), so a new action with no sub-units selected
 * simply delivers to the owner only.
 */
export const getDefaultSubUnitRecipientsForNewAction = (): number[] => [];

/**
 * Reconciles a stored `toSub` list against the sub-units currently attached to
 * the home. Returns the canonicalised (deduped, numeric) selection plus the
 * orphans (ids that no longer exist on the home) so the wizard can prompt the
 * scene author to clean them up.
 */
export const reconcileSubUnitRecipients = (
	toSub: number[] | undefined,
): { selected: number[]; orphans: number[] } => {
	const eligibleIds = new Set<number>();
	for (const key of Object.keys(get(sub_units) ?? {})) {
		const id = parseSubId(key);
		if (id !== undefined) eligibleIds.add(id);
	}

	const seen = new Set<number>();
	const selected: number[] = [];
	const orphans: number[] = [];

	for (const raw of toSub ?? []) {
		const id = Number(raw);
		if (!Number.isFinite(id) || seen.has(id)) continue;
		seen.add(id);
		if (eligibleIds.has(id)) {
			selected.push(id);
		} else {
			orphans.push(id);
		}
	}

	return { selected, orphans };
};

/**
 * Resolves a sub-unit id to a human-readable label. Falls back to
 * `Sub-unit #<id>` when the sub-unit is no longer on the home.
 *
 * The store keys the roster as `sub_<id>`, so we try both the canonical
 * prefixed form and the raw numeric form to stay defensive against any
 * upstream change in key shape.
 */
export const labelForSubUnitId = (subId: number): string => {
	const subs = (get(sub_units) ?? {}) as SubUnitMap;
	const data = subs[`sub_${subId}`] ?? subs[String(subId)];
	if (!data) return `Sub-unit #${subId}`;
	const meta = data[0];
	return getHomeDisplayName(meta?.name, normalize(meta?.email) ?? '') || `Sub-unit #${subId}`;
};

/**
 * Returns true when the home owner is opted-in for the given notification
 * action — i.e. when the action's `pushSkipUnit` flag is not explicitly
 * `true`. Operates on the IoT-script form (post-serialiser) or the wizard's
 * `NotificationActionUI` form interchangeably.
 */
export const actionIncludesOwner = (action: { pushSkipUnit?: boolean } | undefined): boolean =>
	!action?.pushSkipUnit;

/**
 * Inspects a single action element (handles delay-wrapped form) and returns
 * the inner notification body when it is a push/mail action, or `undefined`.
 */
const extractNotificationInner = (element: any): any | undefined => {
	const inner = element?.do?.blk?.[0] ?? element;
	if (!notificationTypeSupportsRecipients(inner?.notify)) return undefined;
	return inner;
};

/**
 * Determines whether the currently logged-in user is targeted by any push or
 * mail notification action on the scene. Powers the "you get notified" badge
 * on the scenes list.
 *
 * Rules:
 * - Owner session → receives when at least one push/mail action does not opt
 *   the owner out via its own `pushSkipUnit` flag.
 * - Sub-unit session → receives when any push/mail action either omits
 *   `toSub` (legacy fallback) or lists the active sub-unit id.
 */
export const currentUserReceivesSceneNotifications = (scene: ISceneObj | undefined): boolean => {
	if (!scene?.do?.length) return false;

	const currentSubId = getCurrentSubUnitId();

	return scene.do.some((element) => {
		const inner = extractNotificationInner(element);
		if (!inner) return false;

		if (currentSubId === undefined) {
			return actionIncludesOwner(inner);
		}

		const toSub = inner?.toSub;
		if (!Array.isArray(toSub)) {
			return true;
		}
		return toSub.some((value: unknown) => Number(value) === currentSubId);
	});
};

/**
 * Walks the scene's `do` array and aggregates the recipients targeted by every
 * push/mail notification action. The result groups by channel so the side-panel
 * preview can render "Phone notifications" and "Email notifications" sections
 * independently.
 *
 * Delay-wrapped notifications (`{ wait, do: { blk: [{ notify, ... }] } }`) are
 * unwrapped automatically.
 */
export const summariseSceneNotificationRecipients = (
	scene: ISceneObj | undefined,
): SceneRecipientSummary => {
	const summary: SceneRecipientSummary = {};
	if (!scene?.do?.length) return summary;

	for (const element of scene.do) {
		const inner = extractNotificationInner(element);
		if (!inner) continue;

		const notify = inner.notify as NotificationActionUI['type'];
		const channel: NotificationChannel = notify === 'push_notification' ? 'P' : 'M';
		const slot = channel === 'P' ? 'push' : 'mail';

		const bucket: ChannelRecipientSummary = summary[slot] ?? {
			channel,
			notificationType: NOTIFICATION_TYPE_BY_CHANNEL[channel],
			ownerIncluded: false,
			subUnits: [],
			orphanSubIds: [],
			legacyAllMembers: false,
			actionCount: 0,
		};

		bucket.actionCount += 1;

		/**
		 * `pushSkipUnit` is per-action; surface owner inclusion at the channel
		 * level by OR-combining: as soon as one action of the channel includes
		 * the owner, the channel is shown as reaching the owner.
		 */
		if (actionIncludesOwner(inner)) {
			bucket.ownerIncluded = true;
		}

		if (Array.isArray(inner.toSub)) {
			const eligibleById = new Map<number, SubUnitRecipientOption>();
			for (const recipient of getEligibleSubUnitRecipients(channel)) {
				eligibleById.set(recipient.subId, recipient);
			}
			const seenIds = new Set<number>(bucket.subUnits.map((r) => r.subId));
			const seenOrphans = new Set<number>(bucket.orphanSubIds);

			for (const raw of inner.toSub) {
				const id = Number(raw);
				if (!Number.isFinite(id)) continue;
				const recipient = eligibleById.get(id);
				if (recipient) {
					if (!seenIds.has(id)) {
						seenIds.add(id);
						bucket.subUnits.push(recipient);
					}
				} else if (!seenOrphans.has(id)) {
					seenOrphans.add(id);
					bucket.orphanSubIds.push(id);
				}
			}
		} else {
			bucket.legacyAllMembers = true;
		}

		summary[slot] = bucket;
	}

	return summary;
};

export type { ChannelRecipientSummary, SceneRecipientSummary } from 'src/types/NotificationRecipients';
